import React from 'react';
import { useAbility } from '@casl/react';
import { useTranslate } from '@bynder/localization';
import { IconImage } from '@bynder/icons';
import { AbilityContext } from '../../../abilities/Can';
import { Highlight, SearchItem, SearchResults } from '../../../types';
import { isModule } from '../../../helpers/windowLocation';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { track } from '../../../helpers/track';
import { Item } from './Item';
import { calculateHeightStyle } from './calculateHeightStyle';
import { iconSizeMedium } from '../../../helpers/iconSizes';

interface Props {
	searchTerm: string;
	results: SearchResults;
	highLight: Highlight;
	defaultSearchAction: () => void;
}

export function GroupAssets({ searchTerm, results, highLight, defaultSearchAction }: Props) {
	const { translate } = useTranslate();
	const { onNewRecentSearch } = useRecentSearches();
	const { assets } = results;
	const ability = useAbility(AbilityContext);

	const canShowRecentSearches = ability.can('show', 'spotlight_recent_searches');

	const totalResults = results.assets.total;

	const onItemClick = (id: string, idHash?: string) => {
		if (!id && !idHash) {
			return;
		}

		if (!id && idHash) {
			document.location.href = '/l/' + idHash;

			return;
		}

		if (isModule('workflow') || isModule('guidelines')) {
			window.location.replace(`/media/?mediaId=${id}`);

			return;
		}

		if (!!highLight && (highLight[id]?.transcription || highLight[id]?.textFromImage)) {
			const mediaModal = window.bynder?.tools?.MediaModal;
			Object.assign(mediaModal as Record<string, unknown>, {
				openAIModal: true,
				searchTranscriptMatch: highLight[id]?.transcription,
			});
		}

		openAssetResult(id);
	};

	const openAssetResult = (id: string) => {
		window.bynder?.tools?.MediaModal?.open(id);

		if (!canShowRecentSearches) {
			return;
		}

		track('Search Executed', {
			product_category: 'Spotlight',
			functional_area: 'Search',
			search_type: 'Spotlight Item Click',
		});

		onNewRecentSearch(searchTerm);
	};

	const onAllMediaTitleClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		defaultSearchAction();
	};

	const getStrMatch = (media: SearchItem) => {
		if (!highLight || !highLight[media.id]) {
			return '';
		}

		const match = highLight[media.id];
		const regex = /<[^>]+>/gm;

		if (match.description && match.description.length) {
			return match.description[0].replace(regex, '');
		}

		if (match.copyright && match.copyright.length) {
			const copyright = match.copyright[0].replace(regex, '');

			return `${translate('GeneralCopyright')}: ${copyright}`;
		}

		if (match.tags && match.tags.length) {
			const tag = match.tags[0].replace(regex, '');

			return `${translate('generalTags')}: ${tag}`;
		}

		if (match.fileContent) {
			return `${translate('spotlight.text')} | "${match.fileContent[0]}"`;
		}

		if (match.transcription) {
			return `${translate('spotlight.transcript')} | "${match.transcription[0]}"`;
		}

		if (match.textFromImage) {
			return `${translate('spotlight.textInImage')}`;
		}

		if (media.description) {
			return media.description.replace(regex, '');
		}

		return '';
	};

	return (
		<>
			{/* @ts-disable */}
			<li className="spotlight-allmedia" onClick={onAllMediaTitleClick}>
				<a href="#" className="header no-click">
					<IconImage size={iconSizeMedium} />
					{translate('generalResultsFor')} <b>{searchTerm}</b>
					<span>{totalResults}</span>
				</a>
			</li>

			<div className="spotlight-results" style={calculateHeightStyle(results)}>
				<ul>
					{assets.items.map((asset) => {
						const displayName = asset.name.substring(0, 34);
						const extensionList = asset.extension?.join(', ') || '';
						const strMatch = getStrMatch(asset);

						return (
							<Item
								item={asset}
								className="spotlight-media"
								dataId={asset.id}
								dataSrc={asset.thumbnails?.large}
								extensions={asset.extension}
								key={asset.id}
							>
								<a
									href="#"
									className="no-click"
									onClick={(e: React.MouseEvent) => {
										e.preventDefault();
										onItemClick(asset.id, asset.idHash);
									}}
								>
									<div className="searchbox-img">
										<img alt="" src={asset.thumbnails?.mini} />
									</div>

									<p className="searchbox-title">{displayName}</p>

									{strMatch.length > 0 && (
										<p className="searchbox-description">
											<em
												// eslint-disable-next-line react/no-danger
												dangerouslySetInnerHTML={{
													__html: strMatch,
												}}
											/>
										</p>
									)}

									{extensionList.length > 0 && (
										<span className="searchbox-info">{extensionList}</span>
									)}
								</a>
							</Item>
						);
					})}
				</ul>
			</div>
		</>
	);
}
